import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import ColumnsHeader from 'pages/ApplicationsPage/components/ColumnsHeader/ColumnsHeader'
import ApplicationsList from 'pages/ApplicationsPage/components/ApplicationsList/ApplicationsList'
import ApplicationRow from 'pages/ApplicationsPage/components/ApplicationRow/ApplicationRow'
import ApplicationEssayReviewDrawer from 'pages/ApplicationsPage/components/ApplicationEssayReviewDrawer/ApplicationEssayReviewDrawer'
import StatusRoadmapView from 'pages/ApplicationsPage/components/StatusRoadmap/StatusRoadmapView'
import { useApplicationsRowViews } from 'pages/ApplicationsPage/hooks/useApplicationsRowViews'
import { useApplicationEssayReviewDrawer } from 'pages/ApplicationsPage/hooks/useApplicationEssayReviewDrawer'
import { refreshAppAssignments } from 'pages/ApplicationsPage/utils/refreshAppAssignments'
import { markApplicationSeenIfNew } from 'pages/ApplicationsPage/utils/markApplicationSeen'
import { applicationDocumentsStore } from 'pages/ApplicationDetailsPage/model/applicationDocumentsStore'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import useStudentApplicationsTab from './hooks/useStudentApplicationsTab'
import s from './styles/StudentApplicationsTab.module.scss'

const StudentApplicationsTab = observer(({ studentId = null, isActive = false }) => {
	const {
		isLoading,
		error,
		rows,
		sortKey,
		sortDir,
		onSortChange,
		onRowNavigate,
		roadmap,
	} = useStudentApplicationsTab(studentId, isActive)

	const { views: rowViews } = useApplicationsRowViews(rows)
	const essayReviewDrawer = useApplicationEssayReviewDrawer()

	const [expandedId, setExpandedId] = useState(null)
	const [assignOpen, setAssignOpen] = useState(false)
	const [assignmentModalRow, setAssignmentModalRow] = useState(null)
	const [applicationModalInstanceKey, setApplicationModalInstanceKey] = useState(0)

	const handleToggleExpand = id => {
		setExpandedId(prev => {
			if (prev === id) return null
			const row = rowViews.find(r => String(r?.id) === String(id))
			if (row?.isNewApplication) {
				void markApplicationSeenIfNew(row)
			}
			return id
		})
	}

	const openAssignmentModal = row => {
		setAssignmentModalRow(row)
		setApplicationModalInstanceKey(k => k + 1)
		setAssignOpen(true)
	}

	if (!studentId) return <div className={s.state}>Student is not selected.</div>
	if (error && rowViews.length === 0)
		return <div className={s.stateError}>{error}</div>

	const showLoader = isLoading && rowViews.length === 0

	return (
		<div className={s.wrap}>
			<div className={s.roadmap}>
				<StatusRoadmapView {...roadmap} />
			</div>

			<div className={s.scroll}>
				<div className={s.inner}>
					<ColumnsHeader
						showSelect={false}
						showStudent={false}
						sortKey={sortKey}
						sortDir={sortDir}
						onSortSelect={onSortChange}
					/>

					{showLoader ? (
						<ApplicationsList>
							<ApplicationRow
								isLoading
								showSelect={false}
								showStudent={false}
							/>
						</ApplicationsList>
					) : (
						<ApplicationsList isEmpty={rowViews.length === 0}>
							{rowViews.map(row => (
								<ApplicationRow
									key={row.id}
									row={row}
									showSelect={false}
									showStudent={false}
									expanded={expandedId === row.id}
									onToggleExpand={handleToggleExpand}
									onOpenApplication={onRowNavigate}
									onAssignAssignment={openAssignmentModal}
									onOpenEssay={essay => essayReviewDrawer.openDrawer(row, essay)}
								/>
							))}
						</ApplicationsList>
					)}
				</div>
			</div>

			<ApplicationEssayReviewDrawer controller={essayReviewDrawer} />

			<AssignActivityModal
				isOpen={assignOpen}
				onClose={() => {
					setAssignOpen(false)
					setAssignmentModalRow(null)
				}}
				selectedStudents={
					assignmentModalRow
						? [
								{
									id:
										assignmentModalRow.studentId ||
										assignmentModalRow.studentName ||
										assignmentModalRow.id,
									name: assignmentModalRow.studentName || 'Student',
									grade: assignmentModalRow.grade || null,
								},
							]
						: []
				}
				initialUniversityId={assignmentModalRow?.universityId || ''}
				initialUniversityName={assignmentModalRow?.university || ''}
				initialUniversityLogoUrl={
					assignmentModalRow?.universityLogoUrl ||
					assignmentModalRow?.logoUrl ||
					''
				}
				initialApplicationTargetId={
					assignmentModalRow?.applicationTargetId ||
					assignmentModalRow?.id ||
					''
				}
				tepeModal="applications"
				applicationModalInstanceKey={applicationModalInstanceKey}
				clearSelectionOnOpen={false}
				lockRecipients
				onSuccess={async () => {
					await refreshAppAssignments()
					await applicationDocumentsStore.reload()
				}}
			/>
		</div>
	)
})

export default StudentApplicationsTab
